import { html, TemplateResult } from 'lit';

import type { PendingSyncRecord } from '@src/modules/kernel/PendingSyncRecord.ts';

export type PendingSyncListPort = {
  loadSyncPending(): Promise<{ entregas: readonly PendingSyncRecord[]; reposiciones: readonly PendingSyncRecord[] }>;
};

/** Shared page shell wrapper class (M3 page enter + max width). */
export const shellPageShellClass = 'mx-auto max-w-3xl space-y-6 px-2 pb-10 sm:px-4';

export const shellSectionClass =
  'md-surface-card bg-[color:var(--md-sys-color-surface-container-high)] p-5';

export async function loadPendingSyncRecordCount(system: PendingSyncListPort): Promise<number> {
  try {
    const counts = await loadSyncIssueCounts(system);
    return counts.total;
  } catch {
    return 0;
  }
}

export async function loadSyncIssueCounts(
  system: PendingSyncListPort,
): Promise<{ pending: number; failed: number; total: number }> {
  try {
    const pendingRows = await system.loadSyncPending();
    let pending = 0;
    let failed = 0;
    for (const row of [...pendingRows.entregas, ...pendingRows.reposiciones]) {
      if (row.syncStatus === 'failed') failed += 1;
      else pending += 1;
    }
    return { pending, failed, total: pending + failed };
  } catch {
    return { pending: 0, failed: 0, total: 0 };
  }
}

export function formatRelativeWhen(iso: string | null): string {
  if (!iso) return 'Nunca';
  try {
    const diffMs = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diffMs / 60_000);
    if (mins < 1) return 'Hace un momento';
    if (mins < 60) return `Hace ${mins} min`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `Hace ${hours} h`;
    const days = Math.floor(hours / 24);
    return `Hace ${days} d`;
  } catch {
    return formatWhen(iso);
  }
}

export function shellEmptyState(p: {
  title: string;
  body: string;
  ctaLabel?: string;
  onCta?: () => void;
}): TemplateResult {
  return html`
    <div class="shell-empty-state flex flex-col items-center px-4 py-8 text-center">
      <span
        class="shell-empty-state__icon mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[color:var(--md-sys-color-primary-container)] text-[color:var(--md-sys-color-on-primary-container)]"
        aria-hidden="true"
      >
        <svg class="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
          <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
        </svg>
      </span>
      <p class="text-base font-medium text-[color:var(--md-sys-color-on-surface)]">${p.title}</p>
      <p class="mt-2 max-w-sm text-sm text-[color:var(--md-sys-color-on-surface-variant)]">${p.body}</p>
      ${p.ctaLabel && p.onCta
        ? html`
            <button type="button" class="md-btn-filled md-sys-ripple touch-manipulation mt-5 text-sm" @click=${p.onCta}>
              ${p.ctaLabel}
            </button>
          `
        : null}
    </div>
  `;
}

export function shellPendingSyncBanner(p: { count: number; syncPath?: string }): TemplateResult {
  if (p.count <= 0) return html``;
  const syncPath = p.syncPath ?? '/sync';
  const label =
    p.count === 1
      ? 'Tiene 1 registro sin enviar al servidor.'
      : `Tiene ${p.count} registros sin enviar al servidor.`;
  return html`
    <div
      class="shell-pending-sync-banner flex flex-wrap items-center justify-between gap-3 rounded-[var(--md-shape-corner-medium)] border border-[color:var(--md-sys-color-primary)]/25 bg-[color:var(--md-sys-color-primary-container)] px-4 py-3 text-sm text-[color:var(--md-sys-color-on-primary-container)]"
      role="status"
    >
      <p class="min-w-0 flex-1">${label}</p>
      <a
        href="${syncPath}"
        class="md-sys-ripple shrink-0 rounded-[var(--md-shape-corner-medium)] px-3 py-1.5 text-sm font-semibold text-[color:var(--md-sys-color-primary)] outline-none hover:bg-[color:var(--md-sys-color-surface-container-high)] focus-visible:ring-2 focus-visible:ring-[color:var(--md-sys-color-primary)]"
      >
        Sincronizar
      </a>
    </div>
  `;
}

export function shellFailedSyncBanner(p: { count: number; syncPath?: string }): TemplateResult {
  if (p.count <= 0) return html``;
  const syncPath = p.syncPath ?? '/sync';
  const label =
    p.count === 1
      ? 'Hay 1 registro con error de sincronización. Revíselo en Sincronización antes de reintentar el envío.'
      : `Hay ${p.count} registros con error de sincronización. Revíselos en Sincronización antes de reintentar el envío.`;
  return html`
    <div
      class="shell-failed-sync-banner flex flex-wrap items-center justify-between gap-3 rounded-[var(--md-shape-corner-medium)] border border-[color:var(--md-sys-color-error)]/35 bg-[color:var(--md-sys-color-error-container)] px-4 py-3 text-sm text-[color:var(--md-sys-color-on-error-container)]"
      role="alert"
    >
      <p class="min-w-0 flex-1">${label}</p>
      <a
        href="${syncPath}"
        class="md-sys-ripple shrink-0 rounded-[var(--md-shape-corner-medium)] px-3 py-1.5 text-sm font-semibold text-[color:var(--md-sys-color-error)] outline-none hover:bg-[color:var(--md-sys-color-surface-container-high)] focus-visible:ring-2 focus-visible:ring-[color:var(--md-sys-color-error)]"
      >
        Ir a sincronización
      </a>
    </div>
  `;
}

export function shellStorageLowBanner(p: { observabilityPath?: string }): TemplateResult {
  const path = p.observabilityPath ?? '/observability';
  return html`
    <div
      class="shell-storage-low-banner flex flex-wrap items-center justify-between gap-3 rounded-[var(--md-shape-corner-medium)] border border-[color:var(--md-sys-color-error)]/35 bg-[color:var(--md-sys-color-error-container)] px-4 py-3 text-sm text-[color:var(--md-sys-color-on-error-container)]"
      role="alert"
    >
      <p class="min-w-0 flex-1">
        El dispositivo tiene poco espacio libre. Exporte registros y libere archivos ya enviados antes de capturar más
        fotos o firmas.
      </p>
      <a
        href="${path}"
        class="md-sys-ripple shrink-0 rounded-[var(--md-shape-corner-medium)] px-3 py-1.5 text-sm font-semibold text-[color:var(--md-sys-color-error)] outline-none hover:bg-[color:var(--md-sys-color-surface-container-high)] focus-visible:ring-2 focus-visible:ring-[color:var(--md-sys-color-error)]"
      >
        Ver almacenamiento
      </a>
    </div>
  `;
}

export function shellExportBackupBanner(p: { message: string; observabilityPath?: string }): TemplateResult {
  const path = p.observabilityPath ?? '/observability';
  return html`
    <div
      class="shell-export-backup-banner flex flex-wrap items-center justify-between gap-3 rounded-[var(--md-shape-corner-medium)] border border-[color:var(--md-sys-color-primary)]/25 bg-[color:var(--md-sys-color-primary-container)] px-4 py-3 text-sm text-[color:var(--md-sys-color-on-primary-container)]"
      role="status"
    >
      <p class="min-w-0 flex-1">${p.message}</p>
      <a
        href="${path}"
        class="md-sys-ripple shrink-0 rounded-[var(--md-shape-corner-medium)] px-3 py-1.5 text-sm font-semibold text-[color:var(--md-sys-color-primary)] outline-none hover:bg-[color:var(--md-sys-color-surface-container-high)] focus-visible:ring-2 focus-visible:ring-[color:var(--md-sys-color-primary)]"
      >
        Exportar respaldo
      </a>
    </div>
  `;
}

export function shellPageErrorBanner(message: string): TemplateResult {
  return html`
    <div
      class="md-error-banner rounded-md px-4 py-3 text-sm"
      style="background: var(--md-sys-color-error-container); color: var(--md-sys-color-on-error-container);"
      role="alert"
    >
      ${message}
    </div>
  `;
}

export function shellPageRefreshBanner(active: boolean): TemplateResult {
  if (!active) return html``;
  return html`
    <p class="text-sm text-[color:var(--md-sys-color-primary)]" role="status" aria-live="polite">
      Actualizando registros…
    </p>
  `;
}

/** Skeleton placeholder while async page content loads. */
export function shellPageLoadingSkeleton(lineCount = 3): TemplateResult {
  return html`
    <div class="${shellPageShellClass}" role="status" aria-busy="true" aria-label="Cargando">
      <div class="md-skeleton md-skeleton--title" aria-hidden="true"></div>
      <div class="md-skeleton md-skeleton--block" aria-hidden="true"></div>
      ${Array.from(
        { length: lineCount },
        () => html`<div class="md-skeleton md-skeleton--line" aria-hidden="true"></div>`,
      )}
    </div>
  `;
}

export function formatByteCount(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function syncProgressPercent(progress: { processed: number; total: number; uploadRatio?: number }): number {
  if (progress.total <= 0) return 0;
  const staged = progress.processed / progress.total;
  const uploadSlice = progress.uploadRatio != null ? progress.uploadRatio / progress.total : 0;
  return Math.min(100, Math.round((staged + uploadSlice) * 100));
}

/** Determinate progress for sync record upload (push). */
export function shellSyncUploadProgress(progress: { processed: number; total: number; uploadRatio?: number }): TemplateResult {
  const pct = syncProgressPercent(progress);
  return html`
    <div
      class="shell-sync-upload-progress mt-3 w-full"
      role="progressbar"
      aria-valuemin="0"
      aria-valuemax="100"
      aria-valuenow=${pct}
      aria-label="Cargando registros"
    >
      <div class="shell-sync-upload-progress__track" aria-hidden="true">
        <div class="shell-sync-upload-progress__fill" style="width: ${pct}%"></div>
      </div>
      <p class="mt-2 text-center text-sm font-medium text-[color:var(--md-sys-color-on-surface)]">${pct}%</p>
    </div>
  `;
}

/** ISO timestamp for system/sync status rows. */
export function formatWhen(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export function shellPageHeader(eyebrow: string, title: string, subtitle?: string): TemplateResult {
  return html`
    <header class="md-page-header">
      <p class="text-xs font-semibold uppercase tracking-wider text-[color:var(--md-sys-color-on-primary-container)]">${eyebrow}</p>
      <h1 class="mt-1 text-[length:1.75rem] font-normal text-[color:var(--md-sys-color-on-surface)]">${title}</h1>
      ${subtitle
        ? html`<p class="mt-2 text-sm text-[color:var(--md-sys-color-on-surface-variant)]">${subtitle}</p>`
        : null}
    </header>
  `;
}
